import React, { useState } from 'react';
import { IDummyProps } from './types';
import { localeDropdown } from './i18n';
import { presets } from './presets';
import { OptionLabel } from './styles';
import { overrides as customOverrides } from './overrides';
import { LocaleProvider, useStyletron } from '../../Themes';
import { mergeOverrides } from 'baseui';
import { Overrides } from 'baseui/overrides';

const SelectComponent = require('baseui/select/select-component').default;
const MultiValue = require('baseui/select/multi-value').default;
const SingleValue = require('baseui/select/value').default;

/**
 * Used to use the expected ref - to be removed when we update bseui library
 * @param refs
 */
function useCombinedRefs(...refs: any) {
	const targetRef = React.useRef(null);

	React.useEffect(() => {
		refs.forEach((ref: any) => {
			if (!ref) return;

			if (typeof ref === 'function') {
				ref(targetRef.current);
			} else {
				ref.current = targetRef.current;
			}
		});
	}, [refs]);

	return targetRef;
}

export const View = React.forwardRef((props: IDummyProps, ref: any) => {
	const {
		error,
		backspaceRemoves = true,
		icon = 'search',
		labelKey = 'label',
		valueKey = 'id',
		positive = false,
		overrides,
		getOptionLabel,
	} = props;
	const [css] = useStyletron();
	const [isExpanded, setIsExpanded] = useState<boolean>(!!props.startOpen);

	const containsNode = (parent: HTMLElement, child: HTMLElement) => {
		return child && parent && parent.contains(child);
	};
	const innerRef = ref?.current;
	const combinedRef = useCombinedRefs(ref, innerRef);
	const mountNodeRef = React.useRef<HTMLSpanElement>(null);

	React.useEffect(() => {
		const localRef: any = combinedRef.current;
		if (localRef) {
			localRef.handleTouchOutside = (event: TouchEvent) => {
				const target = event.composedPath()[0];

				if (containsNode(localRef.dropdown.current, target as HTMLElement)) return;

				if (!containsNode(localRef.anchor.current, target as HTMLElement)) {
					/**
					 * There is no need to reset the input value
					 * https://github.com/uber/baseweb/blob/8d2f903d34c52200603699b2d3c8132e53a990b4/src/select/select-component.js#L187
					 * */
					localRef.props.onClose();
				}
			};

			localRef.handleClickOutside = function(event: MouseEvent) {
				const target = event.composedPath()[0];
				if (localRef.justSelected) {
					localRef.justSelected = false;
					return;
				}
				if (containsNode(localRef.dropdown.current, target as HTMLElement)) {
					return;
				}

				const isFocused = localRef.state.isFocused || localRef.state.isPseudoFocused;
				if (isFocused && !containsNode(localRef.anchor.current, target as HTMLElement)) {
					localRef.handleBlur(event);
				}
			};
		}
	}, [combinedRef]);

	const elementBefore = (_props: any, option: any, item: any) => {
		if (_props.itemBefore) {
			if (_props.preset == 'multi') {
				return item == 'selected'
					? presets(option, _props.textMultiSummary).multi.itemBefore.selected
					: presets(option, _props.textMultiSummary).multi.itemBefore.default;
			}
			return item == 'selected' ? _props.itemBefore.selected : _props.itemBefore.default;
		}
	};

	const getLabel = ({ option }: any) => {
		const itemBefore =
			props.value && props.value.find((element: any) => element.id == option.id) ? (
				<span>{elementBefore(props, option, 'selected')}</span>
			) : (
				<span>{elementBefore(props, option, 'default')}</span>
			);

		return (
			<React.Fragment>
				<div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
					{itemBefore}
					<span>{option.before}</span>
					{/* Note: we need font-family inherit as the selected one
                    is bold and the global styles for the span override this :( */}
					<OptionLabel>{option.label}</OptionLabel>
					<span>{option.after}</span>
				</div>
			</React.Fragment>
		);
	};

	const localOverrides = customOverrides(props, isExpanded, positive, icon, css, mountNodeRef, combinedRef) as any;

	return (
		<LocaleProvider locale={localeDropdown(props)}>
			<div>
				<SelectComponent
					{...props}
					ref={combinedRef}
					valueComponent={props.multi ? MultiValue : SingleValue}
					error={error}
					onBlurResetsInput={false}
					backspaceRemoves={backspaceRemoves}
					getOptionLabel={getOptionLabel || getLabel}
					labelKey={labelKey}
					valueKey={valueKey}
					disabled={props.disabled || props.readOnly}
					onOpen={() => {
						setIsExpanded(true);
						props.onOpen && props.onOpen();
					}}
					onClose={() => {
						setIsExpanded(false);
						props.onClose && props.onClose();
					}}
					overrides={overrides ? mergeOverrides(localOverrides, overrides as Overrides<any>) : localOverrides}
				/>
				<span ref={mountNodeRef}></span>
			</div>
		</LocaleProvider>
	);
});
